/**
 * CR-7194: configurable ASN CSV export.
 *
 * One export button, driven entirely by the company's saved column layout
 * (`column_format`): the visible columns, in their configured order, become the
 * CSV columns. That is what lets a single export serve two very different
 * consumers — a supplier reading the file by eye wants every field with
 * readable headers, while a supplier uploading it into an ERP needs an exact,
 * fixed column set. The latter is configuration (see the
 * `seed_asn_export_layout` management command), not a second code path.
 *
 * Two deliberate differences from the on-screen grid:
 *
 * 1. Values are raw, not display text. The grid renders an unpriced part as an
 *    em-dash and a date as "02 Sep 2026"; a machine-parsed upload needs an
 *    empty cell and an ISO date. So this module has its own value function
 *    rather than reusing `asnCellText`.
 * 2. Headers honour `export_header` when a company has set one, so an ERP that
 *    expects `PONumber` gets exactly that rather than the display label
 *    `PO Number`.
 *
 * Every delivery line is exported, warehouse and direct alike — the export is a
 * data extract, not the SAP clear-the-PO instruction the old direct-only file
 * was, so the direct-only restriction no longer applies.
 *
 * Built entirely from data already in the ASN detail modal; no backend call.
 */
import type { ASNColumnFormatColumn } from '../../lib/api';
import type { ASNData, ASNDeliveryLineDetail } from './asnModalTypes';
import {
  asnPalletCount, asnLineWeight, asnTareWeight, asnGrossWeight, ASN_TARE_DECIMALS,
  asnUnitPrice, asnLineTotal, asnHasPrice, asnPalletQty, asnUnitWeight,
} from './asn-columns';
import { plainDecimal } from '../../lib/plainDecimal';

/** Grid-only action columns carry no exportable value. */
const ACTION_ONLY_COLUMNS = new Set(['attachments']);

/** Quote a CSV field only when it contains a comma, quote or newline. */
function csvCell(value: unknown): string {
  const str = value == null ? '' : String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

/**
 * The columns this CSV will carry: visible, in configured order, excluding
 * action-only columns.
 *
 * @param columnFormat - the ASN's resolved column layout
 * @returns the ordered exportable columns
 */
export function asnCsvColumns(
  columnFormat?: ASNColumnFormatColumn[] | null,
): ASNColumnFormatColumn[] {
  return (columnFormat || [])
    .filter((c) => c.is_visible && !ACTION_ONLY_COLUMNS.has(c.column_id))
    .sort((a, b) => a.sort_order - b.sort_order);
}

/**
 * The raw value for one column on one line — machine-friendly, so a downstream
 * parser gets an empty cell rather than a dash for missing data.
 *
 * @param columnId - registry column id
 * @param line - the delivery line
 * @returns the cell value as a string ('' when absent)
 */
export function asnCsvValue(columnId: string, line: ASNDeliveryLineDetail): string {
  switch (columnId) {
    case 'delivery_number':
      return line.delivery_number ?? '';
    case 'description':
      return line.part_description ?? '';
    case 'part_number':
      return line.part_number ?? '';
    case 'commodity_code':
      return line.commodity_code ?? '';
    case 'po_line':
      if (!line.po_order_number) return '';
      return line.po_line_number
        ? `${line.po_order_number} / ${line.po_line_number}`
        : line.po_order_number;
    case 'po_number':
      return line.po_order_number ?? '';
    case 'po_line_number':
      return line.po_line_number ?? '';
    case 'qty_released':
      return line.qty_released ?? '';
    case 'unit_price':
      return asnHasPrice(line) ? asnUnitPrice(line).toFixed(2) : '';
    case 'line_total':
      return asnHasPrice(line) ? asnLineTotal(line).toFixed(2) : '';
    case 'pieces_per_pallet':
      return asnPalletQty(line) > 0 ? String(line.pallet_qty) : '';
    case 'total_pallets':
      return asnPalletQty(line) > 0 ? String(asnPalletCount(line)) : '';
    // Same precision rule as the grid: weights exactly as held, tare to 6 places.
    case 'net_weight':
      return asnUnitWeight(line) > 0 ? plainDecimal(line.unit_weight_kg) : '';
    case 'line_weight':
      return asnUnitWeight(line) > 0 ? plainDecimal(asnLineWeight(line)) : '';
    case 'tare_weight':
      return asnPalletCount(line) > 0 ? asnTareWeight(line).toFixed(ASN_TARE_DECIMALS) : '';
    case 'gross_weight':
      return asnUnitWeight(line) > 0 || asnPalletCount(line) > 0
        ? plainDecimal(asnGrossWeight(line))
        : '';
    case 'call_off_reference':
      return line.call_off_reference ?? '';
    case 'sales_order':
      return line.sales_order ?? '';
    case 'delivery_option':
      return line.delivery_option ?? '';
    case 'sap_row':
      return line.sap_order_row != null ? String(line.sap_order_row) : '';
    case 'released_date':
      // ISO (YYYY-MM-DD), not the grid's display format — this is parsed.
      return (line.date_released ?? '').slice(0, 10);
    default:
      return '';
  }
}

/**
 * Build the ASN CSV: a header row plus one row per delivery line (CRLF).
 *
 * An ASN with no delivery lines yields the header row alone rather than an
 * empty file, so the shape is still recognisable to whoever opens it.
 *
 * @param asn - the ASN to export
 * @returns the CSV text
 */
export function buildAsnCsv(asn: ASNData): string {
  const cols = asnCsvColumns(asn.column_format);
  const header = cols.map((c) => csvCell(c.export_header || c.label)).join(',');
  const rows = (asn.delivery_lines_details ?? []).map(
    (line) => cols.map((c) => csvCell(asnCsvValue(c.column_id, line))).join(','),
  );
  return [header, ...rows].join('\r\n');
}

/**
 * Trigger a browser download of the ASN CSV.
 *
 * The filename is unchanged from the CR-7142 export this replaces, so any
 * supplier-side habit or pickup rule built around that name keeps working.
 *
 * @param asn - the ASN to export. Saved as `<asn_number>_delivery_notes.csv`.
 */
export function downloadAsnCsv(asn: ASNData): void {
  const blob = new Blob([buildAsnCsv(asn)], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${asn.asn_number || 'ASN'}_delivery_notes.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
